/**
 * key-consequences — what a broken API key actually costs.
 *
 * FEAT-087 / S-376. The handoff asks that a failing key be called out at the
 * top of the screen **and tied to its consequence**: not "the OpenAI key is
 * in error", but "The Long Read is set to OpenAI, so it is silently falling
 * back." A key's status on its own is a fact about the key; the consequence
 * is a fact about the work, and that is the one worth surfacing.
 *
 * Pure, so the rule that decides whether a publication is actually affected
 * is tested rather than eyeballed — the interesting part is the *absence* of
 * a consequence when another scope still has a working key.
 */

export interface KeyLike {
  provider: string;
  status?: string | null;
  /** null = tenant scope, a number = that workspace. */
  agencyclient_id?: number | null;
}

export interface PublicationLike {
  id: number;
  name: string;
  preferredProvider?: string | null;
  preferred_provider?: string | null;
}

/** A key is failing unless it is explicitly active. */
export function isKeyFailing(key: KeyLike): boolean {
  return (key.status ?? "active") !== "active";
}

export function preferredProviderOf(
  pub: PublicationLike,
  workspaceDefault: string | null | undefined
): string | null {
  return pub.preferredProvider ?? pub.preferred_provider ?? workspaceDefault ?? null;
}

export interface KeyConsequence {
  provider: string;
  /** Publications that name this provider and now have no working key. */
  publications: string[];
}

/**
 * Providers that are both **failing and unbacked** — a failing key matters
 * only when no other visible scope still has a working key for the same
 * provider, because the resolver tries workspace then tenant. A tenant key in
 * error while the workspace holds a good one costs nothing, and saying
 * otherwise trains people to ignore the warning.
 *
 * Returns one entry per affected provider, naming the publications that
 * routed to it. A provider with a failing key but no publication pointing at
 * it is still returned, with an empty list: nothing is silently falling back
 * today, but the key is broken and the operator should know.
 */
export function keyConsequences(
  keys: readonly KeyLike[],
  publications: readonly PublicationLike[],
  workspaceDefaultProvider: string | null | undefined
): KeyConsequence[] {
  const failingProviders = new Set(
    keys.filter(isKeyFailing).map((k) => k.provider)
  );
  const workingProviders = new Set(
    keys.filter((k) => !isKeyFailing(k)).map((k) => k.provider)
  );

  const out: KeyConsequence[] = [];
  for (const provider of failingProviders) {
    if (workingProviders.has(provider)) continue; // another scope covers it
    const affected = publications
      .filter((p) => preferredProviderOf(p, workspaceDefaultProvider) === provider)
      .map((p) => p.name);
    out.push({ provider, publications: affected });
  }
  return out.sort((a, b) => a.provider.localeCompare(b.provider));
}
